import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signOut, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { ROLES, isAdminRole, isPartnerRole, canAccessFinance } from '../utils/roles';

const AuthContext = createContext({});

// Users soft-deleted or deactivated in App Users. Their Firebase Auth account
// still exists (deleting it needs the Admin SDK), so access is refused here.
const BLOCKED_STATUSES = ['Deleted', 'Inactive'];
const isBlockedProfile = (profile) => BLOCKED_STATUSES.includes(profile?.status);

const accountDisabledError = () => {
  const error = new Error('This account has been disabled.');
  error.code = 'auth/user-disabled';
  return error;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (userData) => {
    const { email, password, firstName, lastName, role } = userData;
    
    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update user profile with display name
    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`
    });
    
    // Save additional user information to Firestore
    const userDocData = {
      firstName,
      lastName,
      email,
      role: role || 'Partner', // Default to Partner if no role specified
      status: 'Active',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      uid: user.uid
    };
    
    await setDoc(doc(db, 'users', user.uid), userDocData);
    setUserProfile(userDocData);
    
    return userCredential;
  };

  // Sign in function
  const signin = async (email, password) => {
    // Do not toggle global loading here; keep UI in place on error
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Update last login on the user's real profile doc. Writing to users/{uid}
    // directly used to create a lastLogin-only stub for App Users-created
    // users, which then shadowed their real profile (and role).
    const profileDoc = await findUserProfileDoc(userCredential.user.uid);
    if (profileDoc && isBlockedProfile(profileDoc.data())) {
      await signOut(auth);
      throw accountDisabledError();
    }
    if (profileDoc) {
      try {
        await updateDoc(profileDoc.ref, { lastLogin: new Date().toISOString() });
      } catch (error) {
        console.error('Error updating last login:', error);
      }
    }

    // Immediately hydrate context with current user and profile so role checks work without refresh
    setCurrentUser(userCredential.user);
    setUserProfile(profileDoc ? profileDoc.data() : null);

    return userCredential;
  };

  // Sign out function
  const logout = async () => {
    await signOut(auth);
    setCurrentUser(null);
    setUserProfile(null);
  };

  // Find the user's profile doc in Firestore. Users created via signup use the
  // auth uid as the doc id; users created from the App Users module have a
  // random doc id with the uid stored as a field. A users/{uid} doc without a
  // role is a lastLogin-only stub left by older sign-ins, so the uid match wins.
  const findUserProfileDoc = async (uid) => {
    try {
      const byId = await getDoc(doc(db, 'users', uid));
      if (byId.exists() && byId.data()?.role) {
        return byId;
      }
      const snapshot = await getDocs(query(collection(db, 'users'), where('uid', '==', uid)));
      const match = snapshot.docs.find((d) => d.data()?.role) || snapshot.docs[0];
      if (match) {
        return match;
      }
      return byId.exists() ? byId : null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const getUserProfile = async (uid) => {
    const profileDoc = await findUserProfileDoc(uid);
    return profileDoc ? profileDoc.data() : null;
  };

  // Check if user has Super Admin permissions (SA or Developer)
  const isSuperAdmin = () => {
    return isAdminRole(userProfile?.role);
  };

  const isDeveloper = () => {
    return userProfile?.role === ROLES.DEVELOPER;
  };

  // Check if user has Partner permissions (Partner or Member)
  const isPartner = () => {
    return isPartnerRole(userProfile?.role);
  };

  const isMember = () => {
    return userProfile?.role === ROLES.MEMBER;
  };

  // Members get Partner access minus the Finance section
  const canViewFinance = () => {
    return Boolean(userProfile) && canAccessFinance(userProfile.role);
  };

  // Listen for authentication state changes
  useEffect(() => {
    let unsubscribeProfile = null;
    const stopProfileListener = () => {
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      stopProfileListener();
      if (user) {
        setCurrentUser(user);
        const profileDoc = await findUserProfileDoc(user.uid);
        // Auth changed while the profile was loading; the newer callback wins
        if (auth.currentUser?.uid !== user.uid) return;
        if (profileDoc && isBlockedProfile(profileDoc.data())) {
          // Restored session of a deleted/inactive user: end it
          await signOut(auth);
          return;
        }
        setUserProfile(profileDoc ? profileDoc.data() : null);
        if (profileDoc) {
          // Live profile: role changes made in App Users apply without re-login,
          // and a user deleted or deactivated mid-session is signed out
          unsubscribeProfile = onSnapshot(
            profileDoc.ref,
            (snap) => {
              const profile = snap.exists() ? snap.data() : null;
              if (isBlockedProfile(profile)) {
                signOut(auth);
                return;
              }
              setUserProfile(profile);
            },
            (error) => console.error('Error listening to user profile:', error)
          );
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      stopProfileListener();
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    signup,
    signin,
    logout,
    getUserProfile,
    isSuperAdmin,
    isPartner,
    isMember,
    isDeveloper,
    canViewFinance
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 